import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SeriesBadge } from "@/components/SeriesBadge";
import hero from "@/assets/custom-hunting-truck-orange-tarp-high-seats-dog-box-seguin.jpg";
import heroPicture from "@/assets/custom-hunting-truck-orange-tarp-high-seats-dog-box-seguin.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";

export const Route = createFileRoute("/outdoor-recreation-series")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Outdoor Recreation Series — Hunting, Fishing & Boating Covers | Texas Made Shade",
      description:
        "Outdoor Recreation Series — hunting rig tarps, deer blind covers, dog boxes, boat and bimini covers, airboat and console covers built in Seguin, TX for Texas hunters, anglers and boaters.",
      image: hero,
      socialTitle: "Outdoor Recreation Series — Hunting · Fishing · Boating",
      socialDescription:
        "Custom covers, tarps and welded gear for hunting rigs, deer blinds, bay boats, airboats and everything in between.",
      keywords:
        "hunting truck covers texas, deer blind covers, dog boxes, boat covers, bimini tops, airboat covers, fishing boat canvas, hog trap cages, seguin tx hunting fishing covers, hunting rig covers san antonio, boat covers austin, bimini tops texas coast, deer blind covers hill country, airboat covers texas statewide",
    }),
    scripts: serviceHeadScripts({
      path: "/outdoor-recreation-series",
      serviceName: "Outdoor Recreation Series",
      breadcrumbName: "Outdoor Recreation Series",
      description:
        "Hunting, fishing and boating covers — rig tarps, blind covers, dog boxes, bimini tops, boat and airboat canvas built in Texas.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/outdoor-recreation-series"
      serviceName="Outdoor Recreation Series"
      eyebrow="OUTDOOR RECREATION SERIES — FISHING · HUNTING · BOATING"
      title="Outdoor Recreation Series — Hunting, Fishing & Boating Covers"
      intro="For the weekends that matter. The Outdoor Recreation Series builds hunting rig tarps and high seats, deer blind covers, welded dog boxes and hog traps, plus bimini tops, boat covers and airboat canvas — all sewn and welded in Seguin, Texas."
      heroImage={heroPicture}
      preContent={
        <div className="mb-6 flex justify-center">
          <SeriesBadge slug="outdoor-recreation-series" size="md" priority />
        </div>
      }
      heroCta={{
        eyebrow: "Outdoor Recreation Series · Statewide Texas",
        headline: "Gear That Survives the Season.",
        subhead:
          "Send photos and measurements of your rig, blind or boat — we'll quote a custom cover, usually the same business day.",
        primaryHref: "/estimate-quotes#general",
        primaryLabel: "Request Recreation Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      bodyLayout="accordion"
      features={[
        "Hunting truck tarps, high seats and rails",
        "Deer blind covers and fabric blinds",
        "Welded aluminum dog boxes and gear storage",
        "Hog traps, cages and panels",
        "Boat covers, bimini tops and console covers",
        "Airboat, duck boat and kayak canvas",
      ]}
      applications={[
        "Hunting leases, ranches and dove fields",
        "Bay, offshore and freshwater fishing boats",
        "Airboats, duck boats and marsh rigs",
        "Guide services and outfitters",
        "Coolers, gun cases and gear covers",
      ]}
      keywordOutline={[
        { id: "overview", label: "What the Recreation Series Covers" },
        { id: "why-us", label: "Why Hunters & Anglers Call Us" },
        { id: "details", label: "Materials & Fabrication" },
        { id: "applications", label: "Who We Build For" },
        { id: "faqs", label: "Outdoor Recreation FAQs" },
      ]}
      body={[
        {
          heading: "What the Outdoor Recreation Series Covers",
          text: "Anything you haul, hunt or fish out of. Fabric and metal built around your rig instead of a stock size.",
          bullets: [
            "Hunting truck tarps, high seats, rails and racks",
            "Deer blind covers, fabric blinds and window kits",
            "Welded dog boxes, gear boxes and truck storage",
            "Hog traps, cages and welded panels",
            "Boat covers, bimini tops, leaning post and console covers",
            "Airboat, duck boat and kayak canvas",
          ],
        },
        {
          heading: "Materials & Fabrication",
          text: "Marine-grade canvas, heavy vinyl and welded aluminum — the same materials we put on commercial and offshore work, because saltwater and brush don't negotiate.",
          bullets: [
            "Sunbrella® marine canvas and Herculite® vinyl",
            "UV-stable thread, stainless snaps and zippers",
            "In-house aluminum and steel welding",
            "Reinforced corners, webbing and tie-downs",
            "Repairs, recovers and zipper replacement on any brand",
          ],
        },
        {
          heading: "Why Hunters & Anglers Call Us",
          text: "We build for Texas conditions — coastal salt, South Texas brush, and long trailer hauls. Custom-fit, fast turnaround, and a shop that will fix it if the season is rough on it.",
          bullets: [
            "Custom patterned to your boat, blind or truck",
            "Sewing and welding under one roof in Seguin, TX",
            "Statewide service for guides and outfitters",
            "Revival Series repairs when gear takes a beating",
          ],
        },
      ]}
    />
  ),
});
